import { useMutation } from "@tanstack/react-query";
import { useAuth } from "src/components/utils/auth/assist/authContext";
import useAccount from "src/hooks/useAccount";

interface CardPreviewLinkResponse {
  signedUrl: string;
}

export const useInkedCampaignPreviewUrl = () => {
  const { user } = useAuth();
  const { data: accountData } = useAccount();

  return useMutation({
    mutationFn: async (campaignId: number): Promise<string> => {
      // Use different endpoints based on environment
      if (window.location.hostname === "localhost") {
        // Ensure account data is loaded before making the request
        if (!accountData?.customer_id) {
          throw new Error("Account data not loaded yet");
        }

        // On localhost, use the direct Inked API with vendor headers
        const apiUrl = `${process.env.REACT_APP_INKED_API_URL}/vendor/campaign-preview-link`;

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Vendor-Key": process.env.REACT_APP_INKED_VENDOR_KEY || "",
          },
          body: JSON.stringify({
            campaign_id: campaignId,
            user_email: user?.email || "",
            user_external_id: String(accountData.customer_id),
          }),
        });

        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`);
        }

        const data: CardPreviewLinkResponse = await response.json();
        return data.signedUrl;
      } else {
        // In production, use the Landvoice API endpoint
        const response = await fetch("/api/inked/campaign-preview-link", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            campaign_id: campaignId,
          }),
        });

        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`);
        }

        const data: CardPreviewLinkResponse = await response.json();
        return data.signedUrl;
      }
    },
  });
};
